// LUMINA · components/ImageUpload.jsx
// Phase 71: Reusable image upload — drag/click → upload to imgbb → return URL.
// Props: currentUrl, onUpload(url, thumbUrl), label, style

import React, { useState, useRef } from 'react';
import { API } from '../constants/config';

const GD = '#8B6914';

export default function ImageUpload({ currentUrl, onUpload, label = "上传图片", style = {}, compact = false }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Sync preview when currentUrl changes from parent
  React.useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);

  const [dragOver, setDragOver] = useState(false);

  const token = localStorage.getItem('vajra_token');

  const handleFile = async (e) => {
    const file = e.target.files?.[0] || e.dataTransfer?.files?.[0] || (e.clipboardData?.files?.[0]);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('文件过大（最大 10MB）'); return; }
    if (!file.type.startsWith('image/')) { setError('仅支持图片文件'); return; }

    setError('');
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/vajra/staff/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setPreview(data.url);
      onUpload(data.url, data.thumb);
    } catch (err) {
      setError(err.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ ...style }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e); }}
        onPaste={(e) => { handleFile(e); }}
        tabIndex={0}
        style={{
          width: '100%', maxWidth: compact ? 180 : 320, minHeight: compact ? 90 : 120,
          border: `2px dashed ${dragOver ? GD : (preview ? 'rgba(139,105,20,0.3)' : 'rgba(0,0,0,0.15)')}`,
          borderRadius: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: preview ? `url(${preview}) center/cover` : 'rgba(0,0,0,0.02)',
          position: 'relative', overflow: 'hidden',
          transition: 'border-color .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(139,105,20,0.5)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = preview ? 'rgba(139,105,20,0.3)' : 'rgba(0,0,0,0.15)'}
      >
        {preview ? (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 11,
            padding: '4px 8px', textAlign: 'center',
          }}>
            {uploading ? '上传中…' : '点击更换图片'}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, padding: 20 }}>
            {uploading ? '⏳ 上传中…' : <>{label}<br/><span style={{ fontSize: 10 }}>点击或拖拽上传</span></>}
          </div>
        )}
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: '#fff', fontSize: 13 }}>⏳ 上传中…</div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
      {error && <div style={{ color: 'red', fontSize: 11, marginTop: 4 }}>{error}</div>}
      {preview && (
        <button type="button" onClick={() => { setPreview(null); onUpload('', ''); }}
          style={{ display: 'block', marginTop: 4, fontSize: 10, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>
          ✕ 清除图片
        </button>
      )}
    </div>
  );
}
